import React, { useState, useMemo, useCallback, createContext, useContext, useRef, useEffect } from 'react';
import useRequest from '@ahooksjs/use-request';
import { useNavigate } from '@huse/router';
import { get, isArray, isString, omitBy, isEmpty, mergeWith, isObject, isFunction, identity, transform } from 'lodash';
import { Table, Empty } from '@osui/ui';
import * as queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import Error from '@baidu/icloud-ui-error';
import Form from '@baidu/icloud-ui-form';
import { useDerivedState, useLocalStorage, useBoolean } from 'huse';
import { IconFilterOutlined } from '@osui/icons-icloud';
import { renderTypes, columnRenderTypes, columnFilterTypes } from './config';
import { ProTableContext } from './context';
import { Operations } from './Operations';
import { OperationsWrapper } from './OperationsWrapper';
import { OperationLink } from './OperationLink';
import { Tools } from './Tools';
import { Operation } from './Operation';
import { Filters } from './Filters';
import AdvancedFilter from './AdvancedFilters';
import { nestFiltersValue, flattenFiltersValue, calFlattenedDefaultValue, calDefaultColumnFiltersValue, string2hash, diffObj } from './utils';
import './index.less';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export * from './common';
const emptyObject = {};
const GlobalConfigContext = /*#__PURE__*/createContext({});

const useProTableConfig = () => useContext(GlobalConfigContext);

const onFilters = {
  equals: (value, record, name = '') => typeof name === 'string' && record[name] === value || value === 'ALL',
  includes: (value, record, name = '') => {
    var _record$name;

    return !!(typeof name === 'string' && (_record$name = record[name]) !== null && _record$name !== void 0 && _record$name.includes(value)) || value === 'ALL';
  }
}; // eslint-disable-next-line max-statements,complexity

function ProTable(propsIn) {
  var _restFetchOptions$pag;

  const globalConfig = useProTableConfig();
  const props = useMemo(() => mergeWith({ ...propsIn
  }, globalConfig, (propValue, globalValue) => {
    if (!isArray(propValue || globalValue) && !isFunction(propValue || globalValue) && isObject(propValue || globalValue)) {
      return { ...globalValue,
        ...propValue
      };
    }

    return propValue || globalValue;
  }), [propsIn, globalConfig]);
  const {
    request,
    dataSource,
    onChange,
    requestConfig,
    renderTypes,
    syncToSearch = false,
    showTable = true,
    style = emptyObject,
    filters,
    filterType,
    tools,
    operations,
    children,
    onFilterChange,
    columnRenderTypes,
    columns: columnsIn = [],
    columnFilterTypes,
    renderError,
    emptyDescription,
    formProps,
    pagination: paginationIn = {},
    ...restProps
  } = props;
  const location = useLocation();
  const convertNumber = useMemo(() => {
    return filters === null || filters === void 0 ? void 0 : filters.filter(item => {
      return item.isNumber;
    }).map(item => item.name);
  }, [filters]); // 这里只第一次从地址栏拿。没有做成地址栏受控的

  const alternative = useMemo(() => queryString.parse(location.search, {
    arrayFormat: 'bracket'
  }), // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const params = useMemo(() => {
    return transform(alternative, (result, value, key) => {
      if (value && value.length > 0 && convertNumber !== null && convertNumber !== void 0 && convertNumber.includes(key)) {
        result[key] = Array.isArray(value) ? value.map(item => Number(item)) : Number(value);
      } else {
        result[key] = value;
      }
    }, {});
  }, [alternative, convertNumber]); // 从location.search和defaultValue中计算筛选项的初始值

  const initialValues = useMemo(() => {
    if (syncToSearch && filters) {
      return { ...nestFiltersValue(filters, isEmpty(params) ? calFlattenedDefaultValue(filters) : params)
      };
    }

    return filters ? nestFiltersValue(filters, calFlattenedDefaultValue(filters)) : {};
  }, [filters, params, syncToSearch]);
  const [filtersValue, setFiltersValue] = useState(initialValues);
  const localStroageKey = useMemo(() => string2hash(columnsIn.map(i => i.dataIndex || i.title).join(',')).toString(), [columnsIn]);
  const [customerColumnsStorage] = useLocalStorage(localStroageKey, null);
  const [advancedSearchIsExpand, {
    toggle: toggleAdSearchIsExpand
  }] = useBoolean(filterType === 'expanded');
  const oldAdvancedFilterValue = useRef({});
  const [sorter, setSorter] = useState(emptyObject);
  const {
    filterByServer = true,
    pageByServer = true,
    columnFilterByServer,
    pageSizeKey = 'pageSize',
    currentKey = 'page',
    dataKey = 'data',
    totalKey = 'total',
    formatParams = identity,
    ...resetRequestConfig
  } = requestConfig;
  const columnInFilterByServer = columnFilterByServer !== null && columnFilterByServer !== void 0 ? columnFilterByServer : filterByServer;
  const defaultPagination = useMemo(() => {
    const defaultPageSize = props.pagination && props.pagination.defaultPageSize || 10; // 默认值，只取一次

    return {
      current: syncToSearch ? Number(params[currentKey]) || 1 : 1,
      pageSize: syncToSearch ? Number(params[pageSizeKey]) || defaultPageSize : defaultPageSize
    };
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const [pagination, setPagination] = useState(defaultPagination);
  const [columnFilters, setColumnFilters] = useState(calDefaultColumnFiltersValue(columnsIn, columnFilterByServer ? params : {}));
  const history = useHistory();
  const navigate = useNavigate();
  const locale = useMemo(() => ({
    emptyText: /*#__PURE__*/_jsx(Empty, {
      description: emptyDescription,
      style: {
        marginTop: 48,
        marginBottom: 48
      }
    })
  }), [emptyDescription]);
  const syncSearch = useCallback(({
    sorter,
    ...params
  }) => {
    if (syncToSearch) {
      const variable = { ...transform(params, (result, value, key) => {
          result[key] = typeof value === 'object' && isEmpty(value) ? undefined : value;
        }, {}),
        ...(isEmpty(sorter) ? {} : {
          order: sorter.order,
          orderBy: sorter.field
        })
      }; // querstring序列化保存的信息比urlsearchparams更多

      const newLocation = { ...location,
        search: queryString.stringify(variable, {
          arrayFormat: 'bracket'
        })
      };
      navigate(newLocation);
    }
  }, [syncToSearch, location, navigate]);
  const {
    data: fetchResult,
    error,
    loading,
    refresh,
    run,
    ...restFetchOptions
  } = useRequest(({
    current,
    pageSize
  }, {
    columnFilters,
    sorter,
    filtersValue: filtersValueIn = flattenFiltersValue(filters, filtersValue)
  } = {}) => {
    return request ? request(formatParams({ ...params,
      ...columnFilters,
      ...filtersValueIn,
      ...(pageByServer && showTable ? {
        [currentKey]: current,
        [pageSizeKey]: pageSize || 10
      } : {}),
      ...(isEmpty(sorter) ? {} : {
        order: sorter.order,
        orderBy: sorter.column.dataIndex
      })
    })).then(data => isArray(data) ? data : {
      list: data[dataKey],
      total: data[totalKey]
    }) : Promise.resolve(typeof dataSource === 'object' && dataSource !== null && dataSource !== void 0 && dataSource[dataKey] ? {
      list: dataSource[dataKey],
      total: dataSource[totalKey]
    } : dataSource);
  }, {
    defaultParams: [defaultPagination],
    paginated: showTable ? pageByServer : false,
    ...resetRequestConfig,
    refreshDeps: [request, dataSource]
  });
  const handledPagination = pageByServer ? restFetchOptions.pagination : pagination;
  const normalizeFetchRes = useMemo(() => {
    return (isArray(fetchResult) || !showTable ? fetchResult : fetchResult === null || fetchResult === void 0 ? void 0 : fetchResult.list) || [];
  }, [fetchResult, showTable]);
  const fetched = !error && !loading;
  useEffect(() => {
    // 当列表删除一项或者某个操作，列表数据为空的时候，pagniation自动重置到第一页
    if (pageByServer) {
      // 服务端分页
      if (!fetched || !restFetchOptions.pagination) {
        return;
      }

      const current = restFetchOptions.pagination.current;

      if (normalizeFetchRes.length === 0 && current !== 1) {
        run({
          current: 1,
          pageSize: restFetchOptions.pagination.pageSize
        });
      }
    }
  }, [fetched, normalizeFetchRes.length, pageByServer, restFetchOptions, run]);
  const fetchOnChange = (_restFetchOptions$pag = restFetchOptions.pagination) === null || _restFetchOptions$pag === void 0 ? void 0 : _restFetchOptions$pag.onChange;
  const handledPaginations = useMemo(() => {
    if (paginationIn) {
      const mergedPagination = { ...handledPagination,
        ...paginationIn
      };
      return { ...mergedPagination,
        onChange: (current, pageSize) => {
          var _mergedPagination$onC;

          // 这里做了一下限制，因为restFetchOptions.pagination的onChange被调用就会发请求，这样在columnFilters变的时候会发两次请求
          if (mergedPagination.onChange === fetchOnChange) {
            if (current === mergedPagination.current && pageSize === mergedPagination.pageSize) {
              return;
            }
          }

          (_mergedPagination$onC = mergedPagination.onChange) === null || _mergedPagination$onC === void 0 ? void 0 : _mergedPagination$onC.call(mergedPagination, current, pageSize);
        }
      };
    }

    return false;
  }, [fetchOnChange, handledPagination, paginationIn]);
  const handleChange = useCallback((pagination, columnFilters, sorterIn, {
    action
  }) => {
    setColumnFilters(columnFilters);
    const sorter = sorterIn && sorterIn.order ? sorterIn : {};
    setSorter(sorter);
    setPagination(pagination);
    const handledValue = flattenFiltersValue(filters, filtersValue);

    if (columnInFilterByServer && ['sort', 'filter'].includes(action)) {
      run({
        current: pagination.current,
        pageSize: pagination.pageSize
      }, {
        columnFilters,
        sorter,
        filtersValue: handledValue
      });
    }

    syncSearch({ ...columnFilters,
      sorter,
      ...handledValue,
      [currentKey || 'page']: pagination.current || 1,
      [pageSizeKey || 'pageSize']: pagination.pageSize || 10
    });
    onChange && onChange(pagination, columnFilters, sorter);
  }, [onChange, run, filtersValue, currentKey, pageSizeKey, filters, syncSearch, columnInFilterByServer]);
  const unhandledResult = useMemo(() => {
    const arr = normalizeFetchRes;

    if (filterByServer) {
      return arr;
    }

    const handleFilters = filters === null || filters === void 0 ? void 0 : filters.filter(i => i.name && i.onFilter && (typeof i.name === 'string' ? filtersValue[i.name] && filtersValue[i.name] !== 'ALL' : true)).map(i => {
      if (isString(i.onFilter)) {
        return { ...i,
          onFilter: onFilters[i.onFilter]
        };
      }

      return i;
    });

    if (handleFilters && handleFilters.length > 0) {
      return arr.filter(record => handleFilters === null || handleFilters === void 0 ? void 0 : handleFilters.every(filter => typeof filter.onFilter === 'function' && filter.onFilter(get(filtersValue, filter.name), record, filter.name)));
    }

    return arr;
  }, [filterByServer, filters, filtersValue, normalizeFetchRes]);
  const [handledResult, updateTable] = useDerivedState(unhandledResult);
  const context = useMemo(() => {
    return {
      refresh,
      columnFilters,
      filters: filtersValue,
      sorter,
      pagination,
      renderTypes: renderTypes,
      columnRenderTypes,
      columnFilterTypes,
      history,
      data: handledResult,
      updateTable,
      columnsIn,
      loading
    };
  }, [refresh, columnFilters, filtersValue, sorter, pagination, renderTypes, columnRenderTypes, columnFilterTypes, history, handledResult, updateTable, columnsIn, loading]);
  const onSearch = useCallback(allValue => {
    filters === null || filters === void 0 ? void 0 : filters.filter(i => i.name && typeof i === 'string').map(i => {
      if (allValue[i.name]) {
        oldAdvancedFilterValue.current[i.name] = allValue[i.name];
      }

      return null;
    });
    const handledValue = flattenFiltersValue(filters, allValue);
    setPagination({ ...pagination,
      current: 1
    });

    if (filterByServer) {
      run({
        current: 1,
        pageSize: pagination.pageSize
      }, {
        columnFilters,
        sorter,
        filtersValue: handledValue
      });
    }

    syncSearch({
      [currentKey || 'page']: 1,
      [pageSizeKey || 'pageSize']: pagination.pageSize,
      ...columnFilters,
      sorter,
      ...handledValue
    });
    setFiltersValue(allValue);
    onFilterChange && onFilterChange(handledValue);
  }, [filters, pagination, filterByServer, syncSearch, currentKey, pageSizeKey, columnFilters, sorter, onFilterChange, run]);
  const onFormValueChange = useCallback(allValue => {
    const changeField = diffObj(allValue, filtersValue); // 交互逻辑 如果是change 且高级搜索展开了 那么change得字段如果是高级搜索得先不触发

    if (filterType !== 'normal' && advancedSearchIsExpand && filters !== null && filters !== void 0 && filters.find(n => changeField.includes(n.name))) {
      return;
    }

    onSearch({ ...allValue,
      ...(advancedSearchIsExpand ? oldAdvancedFilterValue.current || {} : {})
    });
  }, [filtersValue, filterType, advancedSearchIsExpand, filters, onSearch]);
  const onFormValueSubmit = onSearch;
  const columns = useMemo(() => {
    const tempColumnsIn = tools !== null && tools !== void 0 && tools.find(n => n.renderType === 'customColumns') ? columnsIn.filter(n => // 有缓存找缓存
    customerColumnsStorage ? customerColumnsStorage.find(i => i === (n.dataIndex || n.title)) // 没有缓存找没有设置默认隐藏的字段
    : !n.defaultHidden) : columnsIn;
    return tempColumnsIn.map(i => {
      const {
        renderType,
        filterType
      } = i;
      let newColumn = omitBy(i, [renderType, filterType]);

      if (filterType) {
        newColumn = columnFilterTypes[filterType](i);
        const value = params[newColumn.dataIndex];
        newColumn.defaultFilteredValue = value && (isArray(value) ? value : [value]) || newColumn.defaultFilteredValue;
      } else {
        newColumn.filters = undefined;
      }

      if (i.render) {
        newColumn.render = (value, record, index) => {
          return i.render(value, record, index, { ...context,
            updateRow: func => {
              context.updateTable(data => {
                const newData = [...data];
                newData[index] = func(record);
                return newData;
              });
            }
          });
        };
      }

      if (renderType && !i.render) {
        newColumn.render = (value, record, index) => {
          if (columnRenderTypes[renderType]) {
            return columnRenderTypes[renderType](value, record, index, { ...context,
              column: i,
              updateRow: func => {
                context.updateTable(data => {
                  data[index] = func(record);
                  return data;
                });
              }
            });
          }
        };
      }

      if (i.onFilter) {
        newColumn.onFilter = (value, record) => {
          const filterFunction = typeof i.onFilter === 'string' ? onFilters[i.onFilter] : i.onFilter;
          return typeof filterFunction === 'function' && filterFunction(value, record, i.dataIndex);
        };
      }

      newColumn.filterIcon = () => /*#__PURE__*/_jsx("div", {
        className: "icloud-ui-pro-table-title-icon",
        children: /*#__PURE__*/_jsx(IconFilterOutlined, {})
      });

      return newColumn;
    });
  }, [tools, columnsIn, customerColumnsStorage, columnFilterTypes, params, context, columnRenderTypes]);

  if (error) {
    return renderError ? renderError(error) : /*#__PURE__*/_jsx(Error, {
      error: error.message
    });
  }

  return /*#__PURE__*/_jsx("div", {
    className: "icloud-ui-pro-table",
    style: style,
    children: /*#__PURE__*/_jsxs(ProTableContext.Provider, {
      value: context,
      children: [(filters || tools || operations) && /*#__PURE__*/_jsx(Form, { ...formProps,
        initialValues: initialValues,
        onChange: onFormValueChange,
        onSubmit: onFormValueSubmit,
        children: /*#__PURE__*/_jsxs("div", {
          children: [filterType !== 'normal' && /*#__PURE__*/_jsx(AdvancedFilter, {
            value: filters,
            advancedSearchIsExpand: !!advancedSearchIsExpand,
            toggleAdSearchIsExpand: toggleAdSearchIsExpand
          }), /*#__PURE__*/_jsxs("div", {
            className: "icloud-ui-pro-table-toolbar",
            children: [/*#__PURE__*/_jsx("div", {
              className: "icloud-ui-pro-table-toolbar-left",
              children: operations && /*#__PURE__*/_jsx(Operations, {
                value: operations
              })
            }), /*#__PURE__*/_jsxs("div", {
              className: "icloud-ui-pro-table-toolbar-right",
              children: [filters && filterType === 'normal' && /*#__PURE__*/_jsx(Filters, {
                value: filters
              }), tools && /*#__PURE__*/_jsx(Tools, {
                value: tools
              })]
            })]
          })]
        })
      }), showTable && /*#__PURE__*/_jsx(Table, {
        locale: locale,
        pagination: handledPaginations,
        onChange: handleChange,
        columns: columns,
        dataSource: handledResult,
        loading: loading,
        ...restProps
      }), typeof children === 'function' && children(context)]
    })
  });
}

ProTable.displayName = "ProTable";
ProTable.defaultProps = {
  renderTypes,
  columnRenderTypes,
  columnFilterTypes,
  filterType: 'normal',
  requestConfig: emptyObject,
  renderError: error => {
    return /*#__PURE__*/_jsx(Error, {
      error: error.message
    });
  },
  emptyDescription: /*#__PURE__*/_jsx("div", {
    children: "\u6682\u65E0\u6570\u636E"
  }),

  onFilterChange() {
    return null;
  },

  children() {
    return null;
  }

};
ProTable.OperationsWrapper = OperationsWrapper;
ProTable.Provider = GlobalConfigContext.Provider;
ProTable.OperationLink = OperationLink;
ProTable.Operation = Operation;
export default ProTable;
//# sourceMappingURL=es/index.js.map